import { useEffect, useRef, useState } from "react";
import api from "../lib/api";

/**
 * Iter 40 — Customer name autocomplete for the Manual Booking modal.
 *
 * Behaviour:
 *   • As the operator types (≥2 chars), fetches `/staff/customers/search?q=…`
 *   • Renders a dropdown of matches.
 *   • Selecting a row calls `onSelect(user)` which the parent uses to pre-fill
 *     the Phone (and Email if the form has one).
 *   • Typing OVER a selection re-opens the dropdown so the operator can pick
 *     a different customer.
 */
export default function CustomerNameAutocomplete({ value, onChange, onSelect, className, placeholder, testid = "customer-autocomplete" }) {
    const [suggestions, setSuggestions] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const wrapRef = useRef(null);

    // Debounced remote search
    useEffect(() => {
        const q = (value || "").trim();
        if (q.length < 2) { setSuggestions([]); return; }
        let alive = true;
        const timer = setTimeout(async () => {
            setLoading(true);
            try {
                const { data } = await api.get("/staff/customers/search", { params: { q, limit: 8 } });
                if (alive) { setSuggestions(data || []); setOpen(true); }
            } catch (_) { /* ignore */ }
            finally { if (alive) setLoading(false); }
        }, 200);
        return () => { alive = false; clearTimeout(timer); };
    }, [value]);

    // Close on outside click
    useEffect(() => {
        function onDoc(e) {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        }
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    const pick = (u) => {
        setOpen(false);
        setSuggestions([]);
        onSelect && onSelect(u);
    };

    return (
        <div ref={wrapRef} className="relative" data-testid={testid}>
            <input type="text" value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                onFocus={() => value && value.length >= 2 && setOpen(true)}
                placeholder={placeholder}
                className={className}
                data-testid={`${testid}-input`}
                autoComplete="off" />
            {open && suggestions.length > 0 && (
                <div className="absolute z-30 left-0 right-0 mt-1 max-h-64 overflow-y-auto bg-slate-950 border border-slate-700 rounded shadow-lg"
                    data-testid={`${testid}-list`}>
                    {suggestions.map(u => (
                        <button key={u.id} type="button" onClick={() => pick(u)}
                            className="w-full text-left px-3 py-2 hover:bg-slate-800 border-b border-slate-800 last:border-b-0"
                            data-testid={`${testid}-item-${u.id}`}>
                            <div className="text-sm text-white font-medium">{u.name || "—"}</div>
                            <div className="text-[11px] text-slate-400">
                                {u.phone || <span className="italic text-slate-600">no phone</span>}
                                {u.email ? <span className="ml-2 text-slate-500">· {u.email}</span> : null}
                            </div>
                        </button>
                    ))}
                </div>
            )}
            {open && !loading && suggestions.length === 0 && value && value.length >= 2 && (
                <div className="absolute z-30 left-0 right-0 mt-1 bg-slate-950 border border-slate-700 rounded shadow-lg px-3 py-2 text-xs text-slate-500"
                    data-testid={`${testid}-empty`}>
                    No matches — this will be saved as a new customer.
                </div>
            )}
        </div>
    );
}
